import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

/**
 * Instance configuration (GET /api/instance): names, contact and active
 * modules of the organisation this installation runs for.
 */
export interface InstanceInfo {
  id: string;
  name: {
    long: string;
    short: string;
    location: string;
    app: string;
  };
  appUrl: string;
  contact: {
    email: string;
    website: string;
    phone?: string;
    street?: string;
    zip?: string;
    city?: string;
  };
  branding: {
    subtitle: string;
    welcome: string;
    themeColor: string;
    backgroundColor: string;
  };
  modules: string[];
}

const API_URL = import.meta.env.VITE_API_URL || '/api';

// Neutral values while loading or when the backend is unreachable.
const FALLBACK: InstanceInfo = {
  id: '',
  name: { long: '', short: 'Verwaltung', location: '', app: 'Verwaltung' },
  appUrl: window.location.origin,
  contact: { email: '', website: '' },
  branding: { subtitle: 'Verwaltung', welcome: '', themeColor: '#ffffff', backgroundColor: '#ffffff' },
  modules: [],
};

interface InstanceContextType {
  instance: InstanceInfo;
  isLoading: boolean;
}

const InstanceContext = createContext<InstanceContextType>({ instance: FALLBACK, isLoading: true });

export function InstanceProvider({ children }: { children: React.ReactNode }) {
  const [instance, setInstance] = useState<InstanceInfo | null>(null);

  useEffect(() => {
    axios
      .get<InstanceInfo>(`${API_URL}/instance`)
      .then(res => setInstance(res.data))
      .catch(() => setInstance(FALLBACK));
  }, []);

  // Title and browser chrome follow the instance (index.html only has neutral values).
  useEffect(() => {
    if (!instance) return;
    if (instance.name.short) document.title = instance.name.short;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', instance.branding.themeColor);
    document.querySelector('meta[name="apple-mobile-web-app-title"]')?.setAttribute('content', instance.name.short);
    document.querySelector('meta[name="description"]')?.setAttribute('content', `${instance.name.short} – ${instance.branding.subtitle}`);
  }, [instance]);

  // Render nothing until the instance is known, so no wrong name flashes up.
  if (!instance) return null;

  return <InstanceContext.Provider value={{ instance, isLoading: false }}>{children}</InstanceContext.Provider>;
}

export function useInstance(): InstanceInfo {
  return useContext(InstanceContext).instance;
}
